import type { BadgeVariant } from "@/components/ui/Badge";
import type { InvitationsListResponse } from "@/lib/api/invitations";
import { ApplicationsListResponse } from "@/lib/api/review";
import type { ApplicationDetail } from "@/lib/api/review";
import type { UsersListResponse, AdminUserDetail, UserActivityLogResponse } from "@/lib/api/users";
import type { LocalizationListResponse, LocalizationKeyDetail } from "@/lib/api/localization";
import type {
  VendorDashboardData,
  VendorApplicationData,
  VendorActivityData,
  ApplicationStageId,
} from "@/lib/api/vendor";
import { TranslationKeys } from "@chinese-vendor/i18n";

export type StaffRoleType = "super_admin" | "admin" | "staff";

export const SUPPORT_EMAIL = "vendor.support@aladdinmiles.com";

export const ROLE_LABELS: Record<StaffRoleType, string> = {
  super_admin: "roles.superAdmin",
  admin: "roles.admin",
  staff: "roles.staff",
};

export const ROLE_VARIANTS: Record<StaffRoleType, BadgeVariant> = {
  super_admin: "accent-green",
  admin: "accent-blue",
  staff: "accent-purple",
};

export const DUMMY_APPLICATIONS_DATA: ApplicationsListResponse = {
  items: [
    {
      id: "1",
      company_name: "Desert Bloom Beauty Co.",
      primary_contact_name: "Fatima Zaidi",
      primary_contact_email: "fatima.zaidi@desert-bloom.ae",
      primary_industry: "Consumer Goods",
      status: "pending_review",
      submitted_at: new Date(Date.now() - 86400000).toISOString(),
      vendor_email: "fatima.zaidi@desert-bloom.ae",
      last_activity: new Date(Date.now() - 3600000).toISOString(),
      decision_at: null,
    },
    {
      id: "2",
      company_name: "Olactive Perfumery",
      primary_contact_name: "Marc Leblanc",
      primary_contact_email: "marc.leblanc@olfactive.fr",
      primary_industry: "Luxury Goods",
      status: "pending_review",
      submitted_at: new Date(Date.now() - 86400000).toISOString(),
      vendor_email: "marc.leblanc@olfactive.fr",
      last_activity: new Date(Date.now() - 7200000).toISOString(),
      decision_at: null,
    },
    {
      id: "3",
      company_name: "NovaTek Peripherals",
      primary_contact_name: "Alex Chen",
      primary_contact_email: "alex.chen@novatek-peripherals.com",
      primary_industry: "Technology",
      status: "rejected",
      submitted_at: new Date(Date.now() - 172800000).toISOString(),
      vendor_email: "alex.chen@novatek-peripherals.com",
      last_activity: new Date(Date.now() - 7200000).toISOString(),
      decision_at: new Date(Date.now() - 3600000).toISOString(),
    },
  ],
  total: 14,
  offset: 0,
  limit: 10,
  counts: {
    pending_review: 8,
    approved: 3,
    rejected: 3,
  },
};

export const DUMMY_APPLICATION_DETAIL: ApplicationDetail = {
  id: "dummy-app-001",
  companyName: "Olactive Perfumery",
  status: "pending_review",
  submittedAt: new Date(Date.now() - 7200000).toISOString(),
  legalRepresentative: {
    fullNameChinese: "马克·勒布朗",
    fullNameEnglish: "Marc Leblanc",
    nationality: "French",
    role: "Operations Director",
    department: "Operations and Planning",
    phone: "+86 022 555 0147",
    email: "marc.leblanc@olfactive.fr",
    idType: "Passport",
    idDocumentUrl: "https://example.com/passport.pdf",
    idDocumentSizeLabel: "200 KB",
    wechatQrUrl: undefined,
    wechatQrSizeLabel: undefined,
  },
  businessInformation: {
    companyNameChinese: "嗅觉香水",
    companyNameEnglish: "Olactive Perfumery",
    companyNameArabic: "العطور العطرية",
    registeredAddress: "26F, No. 129, West Yan'an Road, Changning District",
    city: "Shanghai",
    postalCode: "200050",
    ussc: "91310000MA7QRSOP07",
    businessLicenseUrl: "https://example.com/business_license.pdf",
    businessLicenseSizeLabel: "200 KB",
  },
  productCompliance: {
    operatingIndustries: ["Vehicles", "Fashion"],
    operatingCategories: [
      "Automotive",
      "Consumer Electronics",
      "Fashion",
      "Footwear",
      "Home Decor",
      "Textile",
    ],
    isoCertificate: {
      fileName: "doc_name.pdf",
      fileSizeLabel: "200 KB",
      fileUrl: "https://example.com/iso.pdf",
    },
    ceMarking: {
      fileName: "Safety.pdf",
      fileSizeLabel: "200 KB",
      fileUrl: "https://example.com/ce.pdf",
    },
    qualityTestReports: undefined,
    ecoCertifications: [
      { fileName: "FSC.pdf", fileSizeLabel: "200 KB", fileUrl: "https://example.com/fsc.pdf" },
      {
        fileName: "Fair Trade.pdf",
        fileSizeLabel: "200 KB",
        fileUrl: "https://example.com/fairtrade.pdf",
      },
    ],
  },
  financialSettlement: {
    bank: {
      accountHolderName: "Olactive Perfumery (Shanghai)",
      accountNumber: "2003344556677889",
      iban: undefined,
      swiftCode: "HSBCCNSH",
      bankAddress: "33F, HSBC Building, 8 Century Avenue, Pudong, Shanghai 200120",
      settlementCurrency: "CNY (Chinese Yuan)",
    },
    alipay: {
      merchantId: "2088521144332211",
      businessName: "Olactive Perfumery (Shanghai) Co., Ltd.",
      email: "finance@olfactive-perfumery.cn",
      settlementCurrency: "CNY (Chinese Yuan)",
      qrCodeUrl: "https://example.com/alipay-qr.png",
    },
    wechatPay: {
      merchantId: "1612345678",
      businessName: "Olactive Perfumery (Shanghai) Co., Ltd.",
      email: "payments@olfactive-perfumery.cn",
      settlementAccountType: "Digital Wallet Account",
      settlementCurrency: "CNY (Chinese Yuan)",
      qrCodeUrl: "https://example.com/wechat-qr.png",
    },
  },
  brandingLocalization: {
    shortNameEnglish: "Olactive Perfumery",
    shortNameArabic: "تزيين",
    descriptionEnglish:
      "Ornare ut eget risus congue. In eget rhoncus arcu commodo amet sit imperdiet semper. Sit est id dui faucibus accumsan vitae diam vel nulla. Velit feugiat commodo viverra pellentesque.",
    descriptionArabic:
      "أورنار، الهوية التجارية الحصرية لعطور أولكتيف، مكرسة لصنع عطور راقية تلتقط جوهر الطبيعة والأناقة العصرية.",
    logoUrl: "https://example.com/olactive-logo.png",
    logoSizeLabel: "200 KB",
  },
  fieldRejections: {
    unified_social_credit_code: {
      reason:
        "The USCC provided does not match official business registration records. Please provide your 18-digit Unified Social Credit Code as shown on your business license.",
      rejectedAt: new Date(Date.now() - 3600000).toISOString(),
      rejectedBy: { name: "Nina Ross", role: "Admin" },
    },
    eco_certification_document: {
      reason:
        "The uploaded ECO certification document appears to be expired or unreadable. Please upload a valid, current certification (e.g. FSC, Fair Trade, or equivalent).",
      rejectedAt: new Date(Date.now() - 3600000).toISOString(),
      rejectedBy: { name: "Nina Ross", role: "Admin" },
    },
  },
  activityLog: [
    {
      id: "al-1",
      type: "approved",
      actorName: "Nina Ross",
      timestamp: new Date(Date.now() - 10000).toISOString(),
    },
    {
      id: "al-2",
      type: "resubmitted",
      actorName: "Marc Leblanc",
      timestamp: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: "al-3",
      type: "requested_updates",
      actorName: "Adam Zhang",
      timestamp: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: "al-4",
      type: "submitted",
      actorName: "Marc Leblanc",
      timestamp: new Date(Date.now() - 7400000).toISOString(),
    },
  ],
};

export const DUMMY_INVITATION_DATA: InvitationsListResponse = {
  items: [
    {
      id: "1",
      recipientName: "Alex Doe",
      recipientEmail: "alex.doe@example.com",
      invitedByName: "Super Admin",
      invitedByRole: "super_admin",
      sentAt: new Date(Date.now() - 3600000).toISOString(),
      resentCount: 0,
      status: "pending",
    },
    {
      id: "2",
      recipientName: "Jane Smith",
      recipientEmail: "jane.smith@example.com",
      invitedByName: "John Admin",
      invitedByRole: "admin",
      sentAt: new Date(Date.now() - 7200000).toISOString(),
      resentCount: 1,
      status: "accepted",
    },
    {
      id: "3",
      recipientName: null,
      recipientEmail: "unknown@example.com",
      invitedByName: "Jane Smith",
      invitedByRole: "staff",
      sentAt: new Date(Date.now() - 86400000 * 2).toISOString(),
      resentCount: 2,
      status: "expired",
    },
  ],
  total: 3,
  newCount: 0,
  resentCount: 0,
  page: 1,
  pageSize: 10,
};

export const DUMMY_USERS_DATA: UsersListResponse = {
  items: [
    {
      id: "u1",
      name: "Omar Hassan",
      role: "super_admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    },
    {
      id: "u2",
      name: "Wei Chen",
      role: "staff",
      status: "active",
      lastActivityAt: new Date(Date.now() - 13 * 60 * 1000).toISOString(),
    },
    {
      id: "u3",
      name: "Adam Zhang",
      role: "staff",
      status: "active",
      lastActivityAt: new Date(Date.now() - 56 * 60 * 1000).toISOString(),
    },
    {
      id: "u4",
      name: "Nina Ross",
      role: "admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u5",
      name: "Shemar Sanford",
      role: "admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u6",
      name: "Jeffry Christiansen",
      role: "super_admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u7",
      name: "Kirby Buck",
      role: "staff",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u8",
      name: "Jade Chen",
      role: "admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u9",
      name: "Priya Patel",
      role: "super_admin",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u10",
      name: "Ethan Miller",
      role: "staff",
      status: "active",
      lastActivityAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    },
    {
      id: "u11",
      name: "Lena Müller",
      role: "admin",
      status: "inactive",
      lastActivityAt: new Date(Date.now() - 2 * 86400 * 1000).toISOString(),
    },
    {
      id: "u12",
      name: "Carlos Reyes",
      role: "staff",
      status: "inactive",
      lastActivityAt: new Date(Date.now() - 3 * 86400 * 1000).toISOString(),
    },
    {
      id: "u13",
      name: "Aisha Nour",
      role: "staff",
      status: "inactive",
      lastActivityAt: new Date(Date.now() - 7 * 86400 * 1000).toISOString(),
    },
    {
      id: "u14",
      name: "Raj Kapoor",
      role: "staff",
      status: "inactive",
      lastActivityAt: new Date(Date.now() - 14 * 86400 * 1000).toISOString(),
    },
    {
      id: "u15",
      name: "Mei Lin",
      role: "staff",
      status: "disabled",
      lastActivityAt: new Date(Date.now() - 30 * 86400 * 1000).toISOString(),
    },
  ],
  total: 15,
  activeCount: 10,
  inactiveCount: 4,
  disabledCount: 1,
  page: 1,
  pageSize: 10,
};

export const DUMMY_USER_DETAIL: AdminUserDetail = {
  id: "u2",
  name: "Wei Chen",
  role: "staff",
  status: "active",
  lastActivityAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
  firstName: "Wei",
  lastName: "Chen",
  email: "wei@aladdinmiles.com",
  phone: "+86 110 2388 0996",
  languagePreference: "English",
  addedByName: "Priya Patel",
  addedByRole: "super_admin",
  dateAdded: "2026-01-15T11:48:00Z",
  lastModified: "2026-01-15T11:48:00Z",
  profileImageUrl: null,
};

export const DUMMY_USER_ACTIVITY: UserActivityLogResponse = {
  items: [
    {
      id: "a1",
      actorName: "Wei Chen",
      actorRole: "staff",
      action: "approved_application",
      description: "approved Olactive Perfumery application.",
      details: null,
      createdAt: "2026-03-02T14:28:00Z",
    },
    {
      id: "a2",
      actorName: "Nina Ross",
      actorRole: "admin",
      action: "changed_role",
      description: "changed this user's role.",
      fromRole: "admin",
      toRole: "staff",
      details: null,
      createdAt: "2026-03-01T19:58:00Z",
    },
    {
      id: "a3",
      actorName: "Nina Ross",
      actorRole: "admin",
      action: "changed_role",
      description: "changed this user's role.",
      fromRole: "staff",
      toRole: "admin",
      details: null,
      createdAt: "2026-03-01T19:54:00Z",
    },
  ],
};

// ─── Vendor Dashboard ────────────────────────────────────────────────────────

export const DUMMY_VENDOR_DASHBOARD_PENDING: VendorDashboardData = {
  applicationStatus: "pending",
  statusDate: "Mar 02, 2026",
};

export const DUMMY_VENDOR_DASHBOARD_APPROVED: VendorDashboardData = {
  applicationStatus: "approved",
  statusDate: "Mar 04, 2026",
};

export const DUMMY_VENDOR_DASHBOARD_REJECTED: VendorDashboardData = {
  applicationStatus: "rejected",
  statusDate: "Mar 04, 2026",
  rejectionReason:
    "Your application did not meet our compliance requirements for the Luxury Goods category. You are welcome to reapply once you have addressed the compliance documentation.",
  rejectedDocuments: [
    {
      name: "Unified Social Credit Code (USCC)",
      identifier: "12AB34CD56EF78GH9I",
    },
    {
      name: "ECO Certifications",
      file: { name: "fair_trade.pdf", size: "200 KB", url: "https://example.com/fair_trade.pdf" },
    },
  ],
};

export const DUMMY_VENDOR_DASHBOARD_UPDATE_REQUIRED: VendorDashboardData = {
  applicationStatus: "update_required",
  statusDate: "Mar 04, 2026",
  pendingUpdates: [
    {
      name: "Unified Social Credit Code (USCC)",
      category: "Business Information",
      stageId: "business_information",
      fieldKey: "unified_social_credit_code",
    },
    {
      name: "ECO Certifications",
      category: "Product & Compliance",
      stageId: "product_compliance",
      fieldKey: "eco_certification_document",
    },
  ],
};

/** Default placeholder — shown while the /vendors/me/dashboard API is not yet connected. */
export const DUMMY_VENDOR_DASHBOARD_INFO: VendorDashboardData =
  DUMMY_VENDOR_DASHBOARD_UPDATE_REQUIRED;

// ─── Vendor Application ──────────────────────────────────────────────────────

export const DUMMY_VENDOR_APPLICATION_DATA: VendorApplicationData = {
  companyName: "Olactive Perfumery",
  primaryIndustry: "Luxury Goods",
  lastActivity: {
    relative: "2 days ago",
    absolute: "Mar 02, 2026",
    time: "02:28 PM",
  },
  dateSubmitted: {
    relative: "2 days ago",
    absolute: "Mar 02, 2026",
    time: "02:28 PM",
  },
  dateApproved: null,
  stages: [
    { id: "legal_representative", status: "completed" },
    { id: "business_information", status: "update_required" },
    { id: "product_compliance", status: "update_required" },
    { id: "financial_settlement", status: "completed" },
    { id: "branding_localization", status: "completed" },
  ],
};

export const vendorStageLabels: Partial<
  Record<ApplicationStageId, { title: TranslationKeys; subtitle: TranslationKeys }>
> = {
  legal_representative: {
    title: "vendor.application.stages.legalRepresentative",
    subtitle: "preVetting.step1.subtitle",
  },
  business_information: {
    title: "vendor.application.stages.businessInformation",
    subtitle: "preVetting.step2.subtitle",
  },
  product_compliance: {
    title: "vendor.application.stages.productCompliance",
    subtitle: "preVetting.step3.subtitle",
  },
  financial_settlement: {
    title: "vendor.application.stages.financialSettlement",
    subtitle: "preVetting.step4.subtitle",
  },
  branding_localization: {
    title: "vendor.application.stages.brandingLocalization",
    subtitle: "preVetting.step5.subtitle",
  },
};

// ─── Vendor Activity ─────────────────────────────────────────────────────────

/** Activity log shown when the application is in the approved state. */
export const DUMMY_VENDOR_ACTIVITY_APPROVED: VendorActivityData = {
  events: [
    {
      type: "application_approved",
      description: "Your application has been reviewed and approved.",
      date: "Mar 04, 2026",
      time: "02:28 PM",
    },
    {
      type: "application_received",
      description: "All five sections have been submitted",
      date: "Mar 02, 2026",
      time: "02:28 PM",
    },
  ],
};

/** Activity log shown when the application is in the rejected state. */
export const DUMMY_VENDOR_ACTIVITY_REJECTED: VendorActivityData = {
  events: [
    {
      type: "application_rejected",
      description: "Your application has been reviewed and was not approved.",
      date: "Mar 04, 2026",
      time: "02:28 PM",
    },
    {
      type: "application_received",
      description: "All five sections have been submitted",
      date: "Mar 02, 2026",
      time: "02:28 PM",
    },
  ],
};

/** Activity log shown after the vendor has submitted updates following an update_required event. */
export const DUMMY_VENDOR_ACTIVITY_UPDATES_SUBMITTED: VendorActivityData = {
  events: [
    {
      type: "update_submitted",
      description: "Your update to the Eco Certifications has been submitted",
      date: "Mar 04, 2026",
      time: "01:19 PM",
    },
    {
      type: "update_submitted",
      description: "Your update to the Unified Social Credit Code has been submitted",
      date: "Mar 04, 2026",
      time: "01:19 PM",
    },
    {
      type: "update_required",
      description: "2 fields need your attention before the review can continue.",
      date: "Mar 04, 2026",
      time: "01:19 PM",
    },
    {
      type: "application_received",
      description: "All five sections have been submitted",
      date: "Mar 02, 2026",
      time: "02:28 PM",
    },
  ],
};

/** Default placeholder — shown while the /vendors/me/activity API is not yet connected. */
export const DUMMY_VENDOR_ACTIVITY_DATA: VendorActivityData = DUMMY_VENDOR_ACTIVITY_APPROVED;

// TODO: Remove once backend tables are populated and queries are enabled
export const DUMMY_INDUSTRIES_EN = [
  { id: "automotive", name: "Automotive" },
  { id: "baby_maternity", name: "Baby & Maternity" },
  { id: "beauty", name: "Beauty" },
  { id: "biotech_cosmetics", name: "Biotech Cosmetics" },
  { id: "chemicals", name: "Chemicals" },
  { id: "computer_office", name: "Computer & Office Accessories" },
  { id: "consumer_electronics", name: "Consumer Electronics" },
  { id: "customized_pod", name: "Customized & POD Goods" },
  { id: "diy_home", name: "DIY & Home Improvement" },
  { id: "education_stem", name: "Education & STEM Toys" },
  { id: "fashion", name: "Fashion" },
  { id: "footwear", name: "Footwear" },
  { id: "fragrances", name: "Fragrances & Perfumery" },
  { id: "health_supplements", name: "Health Supplements" },
  { id: "home_decor", name: "Home Decor" },
  { id: "home_organization", name: "Home Organization" },
  { id: "jewelry", name: "Jewelry" },
  { id: "kitchen_appliances", name: "Kitchen Appliances" },
  { id: "outdoor_gardening", name: "Outdoor & Gardening" },
  { id: "pet_wellness", name: "Pet Wellness" },
  { id: "sleep_recovery", name: "Sleep & Recovery Gear" },
  { id: "smart_home", name: "Smart Home Devices" },
  { id: "sports_fitness", name: "Sports & Fitness" },
  { id: "textiles", name: "Textiles" },
  { id: "tools_hardware", name: "Tools & Hardware" },
  { id: "toys_games", name: "Toys & Games" },
];

export const DUMMY_INDUSTRIES_ZH = [
  { id: "automotive", name: "汽车" },
  { id: "baby_maternity", name: "婴儿与母婴" },
  { id: "beauty", name: "美妆护肤" },
  { id: "biotech_cosmetics", name: "生物技术化妆品" },
  { id: "chemicals", name: "化学品" },
  { id: "computer_office", name: "电脑及办公配件" },
  { id: "consumer_electronics", name: "消费电子" },
  { id: "customized_pod", name: "定制及按需商品" },
  { id: "diy_home", name: "DIY与家居改善" },
  { id: "education_stem", name: "教育与STEM玩具" },
  { id: "fashion", name: "时尚服饰" },
  { id: "footwear", name: "鞋类" },
  { id: "fragrances", name: "香水与香氛" },
  { id: "health_supplements", name: "健康保健品" },
  { id: "home_decor", name: "家居装饰" },
  { id: "home_organization", name: "家居收纳" },
  { id: "jewelry", name: "珠宝首饰" },
  { id: "kitchen_appliances", name: "厨房电器" },
  { id: "outdoor_gardening", name: "户外与园艺" },
  { id: "pet_wellness", name: "宠物健康" },
  { id: "sleep_recovery", name: "睡眠与恢复装备" },
  { id: "smart_home", name: "智能家居设备" },
  { id: "sports_fitness", name: "运动与健身" },
  { id: "textiles", name: "纺织品" },
  { id: "tools_hardware", name: "工具与五金" },
  { id: "toys_games", name: "玩具与游戏" },
];

export const DUMMY_SUBCATEGORIES_EN = [
  { id: "sc_auto_parts", name: "Auto Parts & Accessories", industryId: "automotive" },
  { id: "sc_car_care", name: "Car Care & Cleaning", industryId: "automotive" },
  { id: "sc_baby_clothing", name: "Baby Clothing", industryId: "baby_maternity" },
  { id: "sc_baby_gear", name: "Baby Gear & Strollers", industryId: "baby_maternity" },
  { id: "sc_skincare", name: "Skincare", industryId: "beauty" },
  { id: "sc_haircare", name: "Haircare", industryId: "beauty" },
  { id: "sc_makeup", name: "Makeup & Cosmetics", industryId: "beauty" },
  { id: "sc_laptops", name: "Laptops & Accessories", industryId: "computer_office" },
  { id: "sc_office_supplies", name: "Office Supplies", industryId: "computer_office" },
  { id: "sc_phones", name: "Smartphones & Accessories", industryId: "consumer_electronics" },
  { id: "sc_audio", name: "Audio & Headphones", industryId: "consumer_electronics" },
  { id: "sc_wearables", name: "Wearables & Smartwatches", industryId: "consumer_electronics" },
  { id: "sc_womens_fashion", name: "Women's Fashion", industryId: "fashion" },
  { id: "sc_mens_fashion", name: "Men's Fashion", industryId: "fashion" },
  { id: "sc_sneakers", name: "Sneakers & Athletic Shoes", industryId: "footwear" },
  { id: "sc_boots", name: "Boots & Formal Shoes", industryId: "footwear" },
  { id: "sc_furniture", name: "Furniture & Shelving", industryId: "home_decor" },
  { id: "sc_wall_art", name: "Wall Art & Décor", industryId: "home_decor" },
  { id: "sc_fine_jewelry", name: "Fine Jewelry", industryId: "jewelry" },
  { id: "sc_costume_jewelry", name: "Fashion Jewelry", industryId: "jewelry" },
  { id: "sc_cookware", name: "Cookware & Bakeware", industryId: "kitchen_appliances" },
  { id: "sc_small_appliances", name: "Small Kitchen Appliances", industryId: "kitchen_appliances" },
  { id: "sc_garden_tools", name: "Garden Tools", industryId: "outdoor_gardening" },
  { id: "sc_pet_food", name: "Pet Food & Treats", industryId: "pet_wellness" },
  { id: "sc_pet_accessories", name: "Pet Accessories", industryId: "pet_wellness" },
  { id: "sc_smart_lighting", name: "Smart Lighting", industryId: "smart_home" },
  { id: "sc_smart_security", name: "Smart Security", industryId: "smart_home" },
  { id: "sc_gym_equipment", name: "Gym & Fitness Equipment", industryId: "sports_fitness" },
  { id: "sc_fabric", name: "Fabric & Raw Textiles", industryId: "textiles" },
  { id: "sc_board_games", name: "Board Games & Puzzles", industryId: "toys_games" },
  { id: "sc_action_figures", name: "Action Figures & Collectibles", industryId: "toys_games" },
];

export const DUMMY_SUBCATEGORIES_ZH = [
  { id: "sc_auto_parts", name: "汽车零部件及配件", industryId: "automotive" },
  { id: "sc_car_care", name: "汽车护理与清洁", industryId: "automotive" },
  { id: "sc_baby_clothing", name: "婴儿服装", industryId: "baby_maternity" },
  { id: "sc_baby_gear", name: "婴儿用品与推车", industryId: "baby_maternity" },
  { id: "sc_skincare", name: "护肤品", industryId: "beauty" },
  { id: "sc_haircare", name: "护发产品", industryId: "beauty" },
  { id: "sc_makeup", name: "彩妆与化妆品", industryId: "beauty" },
  { id: "sc_laptops", name: "笔记本及配件", industryId: "computer_office" },
  { id: "sc_office_supplies", name: "办公用品", industryId: "computer_office" },
  { id: "sc_phones", name: "智能手机及配件", industryId: "consumer_electronics" },
  { id: "sc_audio", name: "音频与耳机", industryId: "consumer_electronics" },
  { id: "sc_wearables", name: "可穿戴设备与智能手表", industryId: "consumer_electronics" },
  { id: "sc_womens_fashion", name: "女装", industryId: "fashion" },
  { id: "sc_mens_fashion", name: "男装", industryId: "fashion" },
  { id: "sc_sneakers", name: "运动鞋", industryId: "footwear" },
  { id: "sc_boots", name: "靴子与正装鞋", industryId: "footwear" },
  { id: "sc_furniture", name: "家具与置物架", industryId: "home_decor" },
  { id: "sc_wall_art", name: "墙饰与装饰品", industryId: "home_decor" },
  { id: "sc_fine_jewelry", name: "精品珠宝", industryId: "jewelry" },
  { id: "sc_costume_jewelry", name: "时尚饰品", industryId: "jewelry" },
  { id: "sc_cookware", name: "炊具与烘焙用具", industryId: "kitchen_appliances" },
  { id: "sc_small_appliances", name: "小型厨房电器", industryId: "kitchen_appliances" },
  { id: "sc_garden_tools", name: "园艺工具", industryId: "outdoor_gardening" },
  { id: "sc_pet_food", name: "宠物食品与零食", industryId: "pet_wellness" },
  { id: "sc_pet_accessories", name: "宠物配件", industryId: "pet_wellness" },
  { id: "sc_smart_lighting", name: "智能照明", industryId: "smart_home" },
  { id: "sc_smart_security", name: "智能安防", industryId: "smart_home" },
  { id: "sc_gym_equipment", name: "健身器材", industryId: "sports_fitness" },
  { id: "sc_fabric", name: "面料与原料纺织品", industryId: "textiles" },
  { id: "sc_board_games", name: "桌游与益智拼图", industryId: "toys_games" },
  { id: "sc_action_figures", name: "手办与收藏品", industryId: "toys_games" },
];

// ─── Localization dummy data ──────────────────────────────────────────────────

/** Shared across all three per-type responses */
const LOCALIZATION_STAT_COUNTS = {
  completedCount: 18,
  pendingCount: 12,
  allCount: 30,
  categoriesCount: 10,
  industriesCount: 10,
  otherCount: 10,
};

/** GET /api/v1/localization/product-categories */
export const DUMMY_LOCALIZATION_CATEGORIES: LocalizationListResponse = {
  ...LOCALIZATION_STAT_COUNTS,
  items: [
    {
      key: "product.category.accessories",
      lastEditedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      translations: { en: "Accessories", "zh-CN": "配件", ar: "إكسسوارات" },
      isComplete: true,
      contentType: "categories",
    },
    {
      key: "product.category.automotive",
      lastEditedAt: null,
      translations: { en: "Automotive", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "categories",
    },
    {
      key: "product.category.baby",
      lastEditedAt: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
      translations: { en: "Baby", "zh-CN": "婴儿用品", ar: "أطفال" },
      isComplete: true,
      contentType: "categories",
    },
    {
      key: "product.category.beauty",
      lastEditedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      translations: { en: "Beauty", "zh-CN": "美妆", ar: "جمال" },
      isComplete: true,
      contentType: "categories",
    },
    {
      key: "product.category.electronics",
      lastEditedAt: null,
      translations: { en: "Electronics", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "categories",
    },
    {
      key: "product.category.home-living",
      lastEditedAt: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
      translations: { en: "Home Living", "zh-CN": "家居", ar: "المنزل" },
      isComplete: true,
      contentType: "categories",
    },
    {
      key: "product.category.games",
      lastEditedAt: null,
      translations: { en: "Games", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "categories",
    },
    {
      key: "product.category.personal-care",
      lastEditedAt: null,
      translations: { en: "Personal Care", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "categories",
    },
    {
      key: "product.category.sports",
      lastEditedAt: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
      translations: { en: "Sports", "zh-CN": "体育", ar: "رياضة" },
      isComplete: true,
      contentType: "categories",
    },
    {
      key: "product.category.toys",
      lastEditedAt: new Date(Date.now() - 7 * 3600 * 1000).toISOString(),
      translations: { en: "Toys", "zh-CN": "玩具", ar: "ألعاب" },
      isComplete: true,
      contentType: "categories",
    },
  ],
  total: 10,
  page: 1,
  pageSize: 10,
};

/** GET /api/v1/localization/industries */
export const DUMMY_LOCALIZATION_INDUSTRIES: LocalizationListResponse = {
  ...LOCALIZATION_STAT_COUNTS,
  items: [
    {
      key: "product.industry.agriculture",
      lastEditedAt: new Date(Date.now() - 1 * 3600 * 1000).toISOString(),
      translations: { en: "Agriculture", "zh-CN": "农业", ar: "الزراعة" },
      isComplete: true,
      contentType: "industries",
    },
    {
      key: "product.industry.automotive",
      lastEditedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      translations: { en: "Automotive", "zh-CN": "汽车", ar: "السيارات" },
      isComplete: true,
      contentType: "industries",
    },
    {
      key: "product.industry.chemicals",
      lastEditedAt: null,
      translations: { en: "Chemicals", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "industries",
    },
    {
      key: "product.industry.construction",
      lastEditedAt: new Date(Date.now() - 3 * 3600 * 1000).toISOString(),
      translations: { en: "Construction", "zh-CN": "建筑", ar: "البناء" },
      isComplete: true,
      contentType: "industries",
    },
    {
      key: "product.industry.consumer-electronics",
      lastEditedAt: null,
      translations: { en: "Consumer Electronics", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "industries",
    },
    {
      key: "product.industry.fashion",
      lastEditedAt: new Date(Date.now() - 5 * 3600 * 1000).toISOString(),
      translations: { en: "Fashion", "zh-CN": "时尚", ar: "الموضة" },
      isComplete: true,
      contentType: "industries",
    },
    {
      key: "product.industry.food-beverage",
      lastEditedAt: null,
      translations: { en: "Food & Beverage", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "industries",
    },
    {
      key: "product.industry.healthcare",
      lastEditedAt: new Date(Date.now() - 8 * 3600 * 1000).toISOString(),
      translations: { en: "Healthcare", "zh-CN": "医疗", ar: "الرعاية الصحية" },
      isComplete: true,
      contentType: "industries",
    },
    {
      key: "product.industry.logistics",
      lastEditedAt: null,
      translations: { en: "Logistics", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "industries",
    },
    {
      key: "product.industry.textiles",
      lastEditedAt: new Date(Date.now() - 10 * 3600 * 1000).toISOString(),
      translations: { en: "Textiles", "zh-CN": "纺织", ar: "المنسوجات" },
      isComplete: true,
      contentType: "industries",
    },
  ],
  total: 10,
  page: 1,
  pageSize: 10,
};

/** GET /api/v1/localization/keys (general dictionary) */
export const DUMMY_LOCALIZATION_OTHER: LocalizationListResponse = {
  ...LOCALIZATION_STAT_COUNTS,
  items: [
    {
      key: "common.back",
      lastEditedAt: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
      translations: { en: "Back", "zh-CN": "返回", ar: "الرجوع" },
      isComplete: true,
      contentType: "other",
    },
    {
      key: "common.cancel",
      lastEditedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      translations: { en: "Cancel", "zh-CN": "取消", ar: "إلغاء" },
      isComplete: true,
      contentType: "other",
    },
    {
      key: "common.changeFile",
      lastEditedAt: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(),
      translations: { en: "Change File", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "other",
    },
    {
      key: "common.error.generic",
      lastEditedAt: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
      translations: { en: "Something went wrong", "zh-CN": "发生错误", ar: "حدث خطأ ما" },
      isComplete: true,
      contentType: "other",
    },
    {
      key: "common.error.network",
      lastEditedAt: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
      translations: { en: "Network error", "zh-CN": "网络错误", ar: "خطأ في الشبكة" },
      isComplete: true,
      contentType: "other",
    },
    {
      key: "common.loading",
      lastEditedAt: new Date(Date.now() - 4 * 3600 * 1000).toISOString(),
      translations: { en: "Loading", "zh-CN": "加载中", ar: "جارٍ التحميل" },
      isComplete: true,
      contentType: "other",
    },
    {
      key: "common.optional",
      lastEditedAt: null,
      translations: { en: "Optional", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "other",
    },
    {
      key: "common.qrScanned",
      lastEditedAt: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
      translations: { en: "QR Scanned", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "other",
    },
    {
      key: "common.rescanQR",
      lastEditedAt: null,
      translations: { en: "Rescan QR", "zh-CN": null, ar: null },
      isComplete: false,
      contentType: "other",
    },
    {
      key: "common.save",
      lastEditedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      translations: { en: "Save", "zh-CN": "保存", ar: "حفظ" },
      isComplete: true,
      contentType: "other",
    },
  ],
  total: 10,
  page: 1,
  pageSize: 10,
};

/** @deprecated Use DUMMY_LOCALIZATION_CATEGORIES / DUMMY_LOCALIZATION_INDUSTRIES / DUMMY_LOCALIZATION_OTHER instead */
export const DUMMY_LOCALIZATION_LIST = DUMMY_LOCALIZATION_CATEGORIES;

export const DUMMY_LOCALIZATION_KEY_DETAIL: LocalizationKeyDetail = {
  key: "search.placeholder",
  lastEditedAt: new Date(Date.now() - 35 * 1000).toISOString(),
  translations: { en: "Placeholder", "zh-CN": null, ar: "عنصر نائب" },
  contentType: "other",
  activityLog: [
    {
      id: "al-1",
      actorName: "Adam Zhang",
      actionType: "edit",
      localeCode: "en",
      localeLabel: "English",
      createdAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
    },
    {
      id: "al-2",
      actorName: "Omar Hassan",
      actionType: "edit",
      localeCode: "en",
      localeLabel: "English",
      createdAt: new Date(Date.now() - 2 * 86400 * 1000).toISOString(),
    },
    {
      id: "al-3",
      actorName: "Adam Zhang",
      actionType: "edit",
      localeCode: "zh-CN",
      localeLabel: "Chinese (Simplified)",
      createdAt: new Date(Date.now() - 3 * 86400 * 1000).toISOString(),
    },
    {
      id: "al-4",
      actorName: "Omar Hassan",
      actionType: "add",
      localeCode: "en",
      localeLabel: "English",
      createdAt: new Date(Date.now() - 5 * 86400 * 1000).toISOString(),
    },
  ],
};
